/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import { ICartlinesViewProps, ICartResources, ICartViewProps, IOrderSummaryErrors } from '@msdyn365-commerce-modules/cart';
import { IOrderSummaryLines } from '@msdyn365-commerce-modules/order-summary-utilities';
import {
    ArrayExtensions,
    getPromotionOptionsForCartLineItems,
    PromotionOptionsForCartLineItems
} from '@msdyn365-commerce-modules/retail-actions';
import {
    Button,
    getPayloadObject,
    getTelemetryAttributes,
    INodeProps,
    ITelemetryContent,
    Node,
    TelemetryConstant
} from '@msdyn365-commerce-modules/utilities';
import { IActionContext } from '@msdyn365-commerce/core';
import { getCartState } from '@msdyn365-commerce/global-state';
import {
    CartLine,
    CartPromotionLine,
    CommerceProperty,
    ProductWarehouseInventoryInformation,
    SimpleProduct
} from '@msdyn365-commerce/retail-proxy';
import { find } from 'lodash';
import * as React from 'react';
import { useForm } from 'react-hook-form';
import { ICartConfig, ICartResources as ICartResourcesExt } from '../definition-extensions/cart.ext.props.autogenerated';

import { IWishlistActionSuccessResult } from '@msdyn365-commerce/components';
import { trackAddToWishlistFromCart, trackBeginCheckout, trackRemoveFromCart, trackViewCart } from '../utilities/datalayer/index';
import { CheckoutType, LoginFromType, SessionStorageKey } from '../utilities/datalayer/types';
import { getEstimatedAvailabilityAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';

interface IPromotionData {
    promotionOptions?: CartPromotionLine[];
}

interface ICartViewPropsExt extends ICartViewProps {
    resources: ICartResourcesExt;
    config: ICartConfig;
}

interface AvailablePromotionsProps {
    props: ICartViewPropsExt;
}

const CartView: React.FC<ICartViewPropsExt> = (props: ICartViewPropsExt) => {
    const [isPickMixOOS, setIsPickMixOOS] = React.useState(false);
    const [pickMixCheckCompleted, setPickMixCheckCompleted] = React.useState(false);
    const [showStickyBar, setShowStickyBar] = React.useState(false);

    const beginClassicCheckout = () => {
        trackBeginCheckout(
            props.context,
            props.cart?.cart,
            props.data.products.result || ([] as SimpleProduct[]),
            CheckoutType.CLASSIC_CHECKOUT
        );

        if (!props.context.request.user.isAuthenticated) {
            sessionStorage.setItem(SessionStorageKey.LOGIN_FROM, LoginFromType.CHECKOUT);
        }
    };

    const beginGuestCheckout = () => {
        trackBeginCheckout(
            props.context,
            props.cart?.cart,
            props.data.products.result || ([] as SimpleProduct[]),
            CheckoutType.GUEST_CHECKOUT
        );
    };

    const trackRemoveFromCartEventHandler = (cartLine: CartLine | undefined) => {
        trackRemoveFromCart(props.context, props.cart?.cart, props.data.products.result || ([] as SimpleProduct[]), cartLine);
    };

    const trackAddToWishlistEventHandler = (cartLine: CartLine | undefined) => {
        trackAddToWishlistFromCart(props.context, props.cart?.cart, props.data.products.result || ([] as SimpleProduct[]), cartLine);
    };

    React.useEffect(() => {
        trackViewCart(props.context, props.cart?.cart, props.data.products.result || ([] as SimpleProduct[]));

        document.querySelector('.msc-cart__btn-checkout')?.addEventListener('click', beginClassicCheckout);
        document.querySelector('.msc-cart__btn-guestcheckout')?.addEventListener('click', beginGuestCheckout);
    }, []);

    const locale = props.context.request.locale;

    React.useEffect(() => {
        let isMounted = true;
        const checkPickMixAvailability = async () => {
            setIsPickMixOOS(false);
            let foundOOS = false;

            const checkPromises =
                props.cartlines
                    ?.filter(cartLine => cartLine.data?.cartline?.TotalAmount !== 0)
                    .flatMap(cartLine => {
                        const pmProductsVal = cartLine?.data?.cartline?.ExtensionProperties?.find(
                            prop => prop.Key === 'PickMixProperty_CAP'
                        )?.Value?.StringValue;

                        const pmProducts = pmProductsVal?.split('</>');

                        if (pmProducts && pmProducts.length > 0) {
                            return pmProducts.map(async pmProduct => {
                                const info = pmProduct.split(';');
                                const pmRecordId = info[4];

                                const productAvailabilites = await getEstimatedAvailabilityAsync(
                                    { callerContext: props.context!.actionContext },
                                    {
                                        ProductIds: [+pmRecordId],
                                        DefaultWarehouseOnly: true,
                                        QuantityUnitTypeValue: 2
                                    }
                                );

                                if (!_isProductAvailable(productAvailabilites)) {
                                    foundOOS = true;
                                }
                            });
                        }

                        return [];
                    }) ?? [];

            await Promise.all(checkPromises);

            if (isMounted) {
                setIsPickMixOOS(foundOOS);
                setPickMixCheckCompleted(true);
            }
        };

        checkPickMixAvailability();

        return () => {
            isMounted = false;
        };
    }, [props.cartlines]);

    React.useEffect(() => {
        const checkoutButton = document.querySelector('.msc-cart__btn-checkout');
        const checkoutGuestButton = document.querySelector('.msc-cart__btn-guestcheckout');

        if (isPickMixOOS) {
            checkoutButton?.setAttribute('disabled', 'true');
            checkoutGuestButton?.setAttribute('disabled', 'true');
        }

        if (!isPickMixOOS) {
            checkoutButton?.removeAttribute('disabled');
            checkoutGuestButton?.removeAttribute('disabled');
        }
    }, [isPickMixOOS]);

    React.useEffect(() => {
        const checkStickyBarVisibility = () => {
            const hasCartItems = (props.cartlines?.length ?? 0) > 0;
            const isStickyBarEnabled = props.config?.checkoutStickybar === true;
            const isMobile = window.innerWidth <= 767; // Mobile breakpoint
            
            setShowStickyBar(isStickyBarEnabled && hasCartItems && isMobile);
        };

        checkStickyBarVisibility();

        window.addEventListener('resize', checkStickyBarVisibility);
        return () => {
            window.removeEventListener('resize', checkStickyBarVisibility);
        };
    }, [props.cartlines, props.config?.checkoutStickybar]);

    const disableCheckout = isPickMixOOS ? { onClick: (e: React.MouseEvent) => e.preventDefault(), href: '#' } : {};

    return (
        <div className={`${props.className} ${showStickyBar ? 'has-sticky-bar' : ''}`} id={props.id} {...props.renderModuleAttributes(props)}>
            {props.title}
            <div className={'row'}>
                {props.multiplePickUpEnabled ? (
                    <RenderCartLinesGroup
                        {...props}
                        trackRemoveFromCartEventHandler={trackRemoveFromCartEventHandler}
                        trackAddToWishlistEventHandler={trackAddToWishlistEventHandler}
                    ></RenderCartLinesGroup>
                ) : (
                    <Node {...props.CartlinesWrapper} className={`col-xl-7 order-2 order-xl-1 ${props.CartlinesWrapper.className}`}>
                        <AvailablePromotions props={props}></AvailablePromotions>
                        <RenderAddGreetingMessage {...props}></RenderAddGreetingMessage>
                        {_renderCartlines(
                            props.cartlines,
                            props.resources,
                            props.storeSelector,
                            props.backToShoppingButton,
                            props.waitingComponent,
                            props.cartLoadingStatus,
                            props.cartDataResult,
                            trackRemoveFromCartEventHandler,
                            trackAddToWishlistEventHandler,
                            props.telemetryContent,
                            props.multiplePickUpEnabled,
                            props.context.actionContext.requestContext.channel?.EmailDeliveryModeCode
                        )}
                    </Node>
                )}
                <div className={'col-xl-4 offset-xl-1 order-1 order-xl-2 msc-order-summary-column'}>
                    {props.orderSummaryHeading && (
                        <Node {...props.OrderSummaryWrapper}>
                            {props.orderSummaryHeading}
                            {_renderOrderSummarylines(props.orderSummaryLineitems, props.OrderSummaryItems, props, locale)}
                            {_renderErrorBlock(props.OrderSummaryErrors)}
                            {pickMixCheckCompleted && isPickMixOOS && _renderPickMixOOSErrorBlock(props.resources)}
                            {React.isValidElement(props.checkoutAsSignInUserButton) &&
                                React.cloneElement(props.checkoutAsSignInUserButton, {
                                    ...disableCheckout
                                })}
                            {React.isValidElement(props.checkoutAsGuestButton) &&
                                React.cloneElement(props.checkoutAsGuestButton, {
                                    ...disableCheckout
                                })}
                            {props.expressCheckoutButton && ArrayExtensions.hasElements(props.expressCheckoutButton) ? (
                                <Node {...props.ExpressCheckoutSectionWrapper}>{props.expressCheckoutButton}</Node>
                            ) : null}
                            {props.backToShoppingButton}
                            {props.createTemplateFromCartButton}
                        </Node>
                    )}
                    {props.storeSelector}
                </div>
            </div>
            
            {showStickyBar && (
                <StickyCheckoutBar
                    checkoutButton={props.checkoutAsSignInUserButton}
                    guestCheckoutButton={props.checkoutAsGuestButton}
                    isPickMixOOS={isPickMixOOS}
                    onCheckoutClick={beginClassicCheckout}
                    onGuestCheckoutClick={beginGuestCheckout}
                    resources={props.resources}
                />
            )}
        </div>
    );
};

const AvailablePromotions: React.FC<AvailablePromotionsProps> = ({ props }) => {
    const [promotionsArray, setPromotionsArray] = React.useState<string[]>([]);
    React.useEffect(() => {
        retrievePromotions(props, setPromotionsArray);
    }, []);

    if (props && props.promotionOptions && promotionsArray.length >= 1) {
        return (
            <React.Fragment>
                <Node {...props.promotionOptions.promotionMessageWrapper}>
                    <span className='msi-promotion-icon' />
                    <span>{props.resources.promotionStringHeading}</span>
                    <ul>
                        {promotionsArray.map(function(text: string, index: number) {
                            return <li key={index}>{text}</li>;
                        })}
                    </ul>
                </Node>
            </React.Fragment>
        );
    } else {
        return <></>;
    }
};

const retrievePromotions = async (cartProps: ICartViewProps, setPromotions: any) => {
    var tmpArray: string[] = [];
    const currentPromotions = await getPromotionData(cartProps.context?.actionContext);
    currentPromotions.promotionOptions?.forEach(element => {
        if (element.Promotion) {
            let offerDesc = '';
            try {
                const offerDescriptions = JSON.parse(element.Promotion.Description || '{}');
                const currentLanguage = cartProps.context.request.locale; // Function to get the current language
                offerDesc = offerDescriptions[currentLanguage.toLowerCase()] || offerDescriptions['it'] || '';
            } catch (e) {
                // If JSON parsing fails, use the OfferDescription as is, split on the colon
                if (element.Promotion.Description) {
                    offerDesc = element.Promotion.Description;
                }
            }
            offerDesc = offerDesc || element.Promotion.OfferName || '';
            if (offerDesc) {
                tmpArray.push(offerDesc);
            }
        }
    });
    setPromotions(tmpArray);
};

async function getPromotionData(ctx: IActionContext): Promise<IPromotionData> {
    const promotionData: IPromotionData = {};
    await getPromotionOptionsForCartLineItems(new PromotionOptionsForCartLineItems(ctx.requestContext.apiSettings), ctx)
        .then(promotionOptions => {
            if (promotionOptions) {
                promotionData.promotionOptions = promotionOptions;
            }
        })
        .catch(error => {
            ctx.telemetry.exception(error);
            return {};
        });
    return promotionData;
}

const RenderAddGreetingMessage = (props: ICartViewPropsExt) => {
    type Inputs = {
        message: string;
        sign: string;
    };

    const { register, handleSubmit, reset, setValue } = useForm<Inputs>();
    // prettier-ignore
    // @ts-ignore
    const extPropsToRemove = props?.cart?.cart?.ExtensionProperties?.filter((fieldVal: { Key: string }) => fieldVal.Key === 'ExtPropsRemove_CAP')[0]?.Value?.BooleanValue;
    const noCartLines = !props?.cart?.cart?.CartLines || props?.cart?.cart?.CartLines.length === 0;
    // prettier-ignore
    // @ts-ignore
    const greetingMessageCAP = (noCartLines || extPropsToRemove) ? '' : props?.cart?.cart?.ExtensionProperties?.filter((greetingVal: { Key: string }) => greetingVal.Key === 'GreetingMessage_CAP')[0]?.Value?.StringValue;
    // prettier-ignore
    // @ts-ignore
    const greetingSignCAP = (noCartLines || extPropsToRemove) ? '' : props?.cart?.cart?.ExtensionProperties?.filter((greetingVal: { Key: string }) => greetingVal.Key === 'GreetingSign_CAP')[0]?.Value?.StringValue;

    // @ts-ignore
    const setRemoveData = async (cartprops: ICartViewProps) => {
        // prettier-ignore
        // @ts-ignore
        const currentCartState = await getCartState(cartprops.context?.actionContext);
        // const currentCartState = props?.cart;
        const extProps = currentCartState?.cart?.ExtensionProperties;
        const extPropsToEmpty = [];
        // const cartLines = currentCartState?.cart?.CartLines;
        // if (extPropsToRemove === true || !cartLines || (cartLines && cartLines.length === 0)) {
        if (extPropsToRemove === true) {
            const extKeysToRemove = [
                'ActiveCartId_CAP',
                'CheckoutCartId_CAP',
                'GreetingMessage_CAP',
                'GreetingSign_CAP',
                'billing',
                'Type_CAP',
                'Country_CAP',
                'City_CAP',
                'ZipCode_CAP',
                'OrgName_CAP',
                'VatNum_CAP',
                'FiscalCode_CAP',
                'PEC_CAP',
                'SDI_CAP',
                'InvoiceCustAccount_CAP',
                'Street_CAP',
                'StreetNumber_CAP',
                'FirstName_CAP',
                'LastName_CAP'
            ];

            if (extProps) {
                for (const propToRemove of extKeysToRemove) {
                    if (find(extProps, (obj: { Key: string }): boolean => obj.Key === propToRemove)) {
                        extPropsToEmpty.push({
                            Key: propToRemove,
                            Value: {}
                        });
                    }
                }
                if (extPropsToRemove === true) {
                    extPropsToEmpty.push({
                        Key: 'ExtPropsRemove_CAP',
                        Value: {
                            BooleanValue: false
                        }
                    });
                }
            }
            if (currentCartState && extPropsToEmpty.length !== 0) {
                await currentCartState.updateExtensionProperties({ newExtensionProperties: extPropsToEmpty });
            }
            // else {
            //     console.log("no extension property to clean found");
            // }
        } else {
            setValue('message', greetingMessageCAP, { shouldDirty: true });
            setValue('sign', greetingSignCAP, { shouldDirty: true });
        }
    };
    React.useEffect(() => {
        void setRemoveData(props);
    }, []);
    const [greetingsState, setGreetingsState] = React.useState({
        isCompiled: false,
        isEditing: false
    });
    const [greeting, setGreeting] = React.useState({
        message: '',
        sign: ''
    });

    React.useEffect(() => {
        setGreetingsState(greetingsState => ({
            ...greetingsState,
            isCompiled: (greetingMessageCAP && greetingMessageCAP !== '') || (greetingSignCAP && greetingSignCAP !== '') ? true : false
        }));
        setGreeting({
            message: greetingMessageCAP && greetingMessageCAP !== '' ? greetingMessageCAP : '',
            sign: greetingSignCAP && greetingSignCAP !== '' ? greetingSignCAP : ''
        });
    }, [greetingMessageCAP, greetingSignCAP]);

    const greetingsTrigger = () => {
        setGreetingsState(greetingsState => ({
            ...greetingsState,
            isEditing: !greetingsState.isEditing
        }));
    };

    const updateExtProps = async (data: Inputs, cartProps: ICartViewProps) => {
        const addGreeting = [
            {
                Key: 'GreetingMessage_CAP',
                Value: {
                    StringValue: data.message
                }
            },
            {
                Key: 'GreetingSign_CAP',
                Value: {
                    StringValue: data.sign
                }
            }
        ];

        const currentCartState = await getCartState(cartProps.context?.actionContext);
        await currentCartState.updateExtensionProperties({ newExtensionProperties: addGreeting });
        await currentCartState.refreshCart({});
    };

    const _addMessCart = async (data: Inputs) => {
        setGreeting(() => ({
            message: data.message,
            sign: data.sign
        }));
        if (data.message !== '' || data.sign !== '') {
            setGreetingsState(() => ({
                isCompiled: true,
                isEditing: false
            }));
        } else {
            setGreetingsState(() => ({
                isCompiled: false,
                isEditing: false
            }));
        }

        await updateExtProps(data, props);
    };

    const resetGreeting = () => {
        reset({ message: greeting.message, sign: greeting.sign });
        setGreetingsState(() => ({
            isCompiled: true,
            isEditing: false
        }));
    };
    const changeGreeting = () => {
        setGreetingsState(() => ({
            isCompiled: false,
            isEditing: true
        }));
    };
    const removeGreeting = async () => {
        reset({ message: '', sign: '' });
        setGreeting({ message: '', sign: '' });
        setGreetingsState(() => ({
            isCompiled: false,
            isEditing: false
        }));
        await updateExtProps({ message: '', sign: '' }, props);
    };
    return (
        <div
            className={`msc-greetings is--${greetingsState.isEditing ? 'editing' : ''} is--${greetingsState.isCompiled ? 'compiled' : ''}`}
        >
            <h4 className='msc-greetings__title' onClick={greetingsTrigger}>
                <span className={'icon-gift'} />
                {props.resources.addGreetingText}
            </h4>
            <div className={`greetings_ctn`}>
                <form className={'msc-greetings-form'} onSubmit={handleSubmit(_addMessCart)}>
                    {props.config.invoiceNotIncludedLabel && (
                        <h4 className='msc-greetings-form__invoice-not-included'>{props.config.invoiceNotIncludedLabel}</h4>
                    )}
                    <div className={'msc-greetings-form__item'}>
                        <label htmlFor='message'>{props.resources.greetingMessage}</label>
                        <textarea name='message' maxLength={700} ref={register()} />
                    </div>
                    <div className={'msc-greetings-form__item'}>
                        <label htmlFor='sign'>{props.resources.greetingSign}</label>
                        <input name='sign' maxLength={35} ref={register()} />
                    </div>
                    <div className={'msc-greetings-form__actions_ctn'}>
                        <button className={'cancel'} onClick={resetGreeting}>
                            {props.resources.greetingDiscard}
                        </button>
                        <button type='submit' className={'update'}>
                            {props.resources.greetingUpdate}
                        </button>
                    </div>
                </form>
                <div className={'msc-greetings_compiled'}>
                    <div>
                        <label>{props.resources.greetingMessage}</label> {greeting.message}
                    </div>
                    <div>
                        <label>{props.resources.greetingSign}</label> {greeting.sign}{' '}
                    </div>
                    <div className={'msc-greetings_compiled_actions'}>
                        <button onClick={changeGreeting}>{props.resources.editBtnLabel}</button>
                        <button onClick={removeGreeting}>{props.resources.addressRemoveButtonText}</button>
                    </div>
                </div>
            </div>
        </div>
    );
};

const _isProductAvailable = (productAvailabilites: ProductWarehouseInventoryInformation): boolean => {
    if (!productAvailabilites?.ProductWarehouseInventoryAvailabilities?.length) {
        return false;
    }

    const inventory = productAvailabilites.ProductWarehouseInventoryAvailabilities[0];
    const maxQuantity = inventory.MaximumPurchasablePhysicalAvailableQuantity || 0;

    if (maxQuantity >= 1) {
        return true;
    } else {
        return false;
    }
};

const _renderCartlines = (
    cartLines: ICartlinesViewProps[] | undefined,
    resources: ICartResources,
    storeSelector: React.ReactNode | undefined,
    backToShoppingButton: React.ReactNode,
    waitingComponent: React.ReactNode,
    cartLoadingStatus: string,
    cartDataResult: boolean,
    trackRemoveFromCartEventHandler: (cartLine: CartLine | undefined) => void,
    trackAddToWishlistEventHandler: (cartLine: CartLine | undefined) => void,
    telemetryContent?: ITelemetryContent,
    mulitplePickUp?: boolean,
    emailDeliveryModeCode?: string,
    context?: ICartViewPropsExt['context']
): JSX.Element[] | JSX.Element => {
    const { emptyCartText } = resources;

    if (cartLoadingStatus) {
        return <>{cartLoadingStatus}</>;
    }

    // GA
    if (cartLines) {
        cartLines = cartLines?.map(cartLine => {
            const remove = _renderRemoveButton(cartLine, trackRemoveFromCartEventHandler);
            const addToWishlist = _renderWishlistButton(cartLine, trackAddToWishlistEventHandler);

            return {
                ...cartLine,
                remove,
                addToWishlist
            };
        });

        const freeCartLines = cartLines.filter(cartLine => cartLine.data?.cartline?.TotalAmount === 0);
        const paidCartLines = cartLines
            .filter(cartLine => cartLine.data?.cartline?.TotalAmount !== 0)
            .sort((a, b) => {
                // Extract GwpItem property value
                const aIsGwpItem = a.data?.cartline?.ExtensionProperties?.some(
                    (prop: CommerceProperty) => prop.Key === 'GWPItem' && prop.Value?.BooleanValue === true
                )
                    ? 1
                    : 0;

                const bIsGwpItem = b.data?.cartline?.ExtensionProperties?.some(
                    (prop: CommerceProperty) => prop.Key === 'GWPItem' && prop.Value?.BooleanValue === true
                )
                    ? 1
                    : 0;

                // Sort logic
                return bIsGwpItem - aIsGwpItem;
            });

        const freeCartLinesTsx = freeCartLines.map(cartLine => {
            const isGWPAddedAutomatically = cartLine.data?.cartline.ExtensionProperties?.some(
                (prop: CommerceProperty) => prop.Key === 'GWPType' && prop.Value?.StringValue === 'AUTO'
            )
                ? true
                : false;
            const isItemFree = cartLine.data?.cartline?.TotalAmount === 0;

            return (
                <div className='msc-cart-lines-item free-item' key={cartLine.data!.cartline!.LineNumber!}>
                    <div className='msc-cart-lines-item__inner'>
                        {cartLine.cartline}
                        {mulitplePickUp
                            ? null
                            : _renderBOPISBlock(cartLine, resources, storeSelector, telemetryContent, emailDeliveryModeCode)}
                        {mulitplePickUp ? (
                            // OLD CLASS: msc-cart-line-group__extra-actions
                            <Node className='msc-cart-line__extra-actions'>
                                {_renderGroupBOPISBlock(cartLine, resources, storeSelector, telemetryContent, emailDeliveryModeCode)}
                                {cartLine.hasError || !(isGWPAddedAutomatically || isItemFree) ? cartLine.remove : null}
                            </Node>
                        ) : (
                            <Node className='msc-cart-line__extra-actions'>
                                {cartLine.hasError || !(isGWPAddedAutomatically || isItemFree) ? cartLine.remove : null}
                            </Node>
                        )}
                    </div>
                </div>
            );
        });

        const paidCartLinesTsx = paidCartLines.map(cartLine => {
            // prettier-ignore
            // @ts-ignore
            const pmProductsVal = cartLine?.data?.cartline?.ExtensionProperties?.filter((fieldVal: { Key: string }) => fieldVal.Key === 'PickMixProperty_CAP')[0]?.Value?.StringValue;
            const pmProducts = pmProductsVal?.split('</>');

            const isPmProduct = pmProducts && pmProducts.length > 0 && true;
            return (
                <div className='msc-cart-lines-item' key={cartLine.data!.cartline!.LineNumber!}>
                    <div className='msc-cart-lines-item__inner'>
                        {React.isValidElement(cartLine.cartline) && React.cloneElement(cartLine.cartline, { remove: cartLine.remove })}
                        {mulitplePickUp
                            ? null
                            : _renderBOPISBlock(cartLine, resources, storeSelector, telemetryContent, emailDeliveryModeCode)}
                        {mulitplePickUp ? (
                            // OLD CLASS: msc-cart-line-group__extra-actions
                            <Node className='msc-cart-line__extra-actions'>
                                {_renderGroupBOPISBlock(cartLine, resources, storeSelector, telemetryContent, emailDeliveryModeCode)}
                                {!isPmProduct && cartLine.addToWishlist}
                                {/* {cartLine.remove} */}
                            </Node>
                        ) : (
                            <Node className='msc-cart-line__extra-actions'>
                                {!isPmProduct && cartLine.addToWishlist}
                                {/* {cartLine.remove} */}
                            </Node>
                        )}
                    </div>
                </div>
            );
        });
        //TODO
        return [...freeCartLinesTsx, ...paidCartLinesTsx];
        // return freeCartLinesTsx;
    } else {
        return cartDataResult ? (
            <div className='msc-cart__empty-cart'>
                <p className='msc-cart-line'>{emptyCartText}</p>
                {backToShoppingButton}
            </div>
        ) : (
            <>{waitingComponent}</>
        );
    }
};

const _renderBOPISBlock = (
    cartLine: ICartlinesViewProps,
    resources: ICartResources,
    storeSelector: React.ReactNode | undefined,
    telemetryContent?: ITelemetryContent,
    emailDeliveryModeCode?: string
): JSX.Element | null => {
    // If it is electronic item cart line, then return null for BOPISBlock.
    if (!cartLine.pickUpInStore || !storeSelector || cartLine.data?.cartline.DeliveryMode === emailDeliveryModeCode) {
        return null;
    }

    const {
        shipInsteadDisplayText,
        shipToAddressDisplayText,
        pickItUpDisplayText,
        pickUpAtStoreWithLocationText,
        changeStoreDisplayText
    } = resources;

    const isBopisSelected = cartLine.pickUpInStore.isBopisSelected;
    const toggleBopis = (event: React.MouseEvent<HTMLElement>) => {
        return cartLine.pickUpInStore && cartLine.pickUpInStore.callbacks.toggleBopis(!isBopisSelected);
    };
    const changeStore = (event: React.MouseEvent<HTMLElement>) => {
        return cartLine.pickUpInStore && cartLine.pickUpInStore.callbacks.toggleBopis(true);
    };
    const payLoad = getPayloadObject('click', telemetryContent!, TelemetryConstant.PickupInStore);
    const puckUpinStoreAttribute = getTelemetryAttributes(telemetryContent!, payLoad);

    return (
        <Node {...cartLine.pickUpInStore.ContainerProps}>
            <div className='msc-cart-line__bopis-method'>
                {isBopisSelected ? (
                    <span className='pick-up'>{pickUpAtStoreWithLocationText}</span>
                ) : (
                    <span className='ship'>{shipToAddressDisplayText}</span>
                )}
            </div>
            {isBopisSelected && (
                <div className='msc-cart-line__bopis-fullfilment'>
                    <span className='msc-cart-line__bopis-fullfilment-store'>{cartLine.pickUpInStore.orgUnitName}</span>
                    <Button className='msc-cart-line__bopis-changestore' onClick={changeStore}>
                        {changeStoreDisplayText}
                    </Button>
                </div>
            )}
            <Button className='msc-cart-line__bopis-btn' {...puckUpinStoreAttribute} onClick={toggleBopis}>
                {isBopisSelected ? shipInsteadDisplayText : pickItUpDisplayText}
            </Button>
        </Node>
    );
};

const _renderErrorBlock = (errorData: IOrderSummaryErrors | undefined): JSX.Element | null => {
    if (!errorData || errorData.errors.length === 0) {
        return null;
    }
    return (
        <Node {...errorData.Wrapper}>
            {errorData.header}
            {errorData.errors}
        </Node>
    );
};

const _renderPickMixOOSErrorBlock = (resources: ICartResourcesExt) => {
    return (
        <div
            className='msc-alert msc-alert msc-alert-danger'
            role='alert'
            aria-live='assertive'
            aria-label={resources.outOfStockErrorMessageHeader}
        >
            <div className='fade show'>
                <div className='msc-alert__header' aria-hidden='true'>
                    <span className='msi-exclamation-triangle'></span>
                    <span>{resources.outOfStockErrorMessageHeader}</span>
                </div>
                <div className='msc-alert__line'>{resources.pickMixOutOfStockErrorMessage}</div>
            </div>
        </div>
    );
};

const _renderCustomTaxLine = (props: ICartViewProps): JSX.Element | null => {
    const { estimatedTaxLabel, yetToBeCalculatedText } = props.resources;

    return (
        <div style={{ marginBottom: '1rem' }}>
            <p className='msc-order-summary__line-tax'>
                <span className='msc-order-summary__label'>{estimatedTaxLabel}</span>
                <span className='msc-order-summary__value'>{yetToBeCalculatedText}</span>
            </p>
        </div>
    );
};

const _renderOrderSummarylines = (
    orderSummaryLines: IOrderSummaryLines | undefined,
    OrderSummaryItems: INodeProps,
    props: ICartViewPropsExt,
    locale: string
): JSX.Element | null => {
    if (!orderSummaryLines) {
        return null;
    }

    const couponCustomErrors = {
        expired: props.resources.couponErrorExpired,
        invalid: props.resources.couponErrorInvalid,
        minimumTreshold: props.resources.couponErrorMinimumTreshold,
        userRequired: props.resources.couponErrorLogin
    };

    return (
        <Node {...OrderSummaryItems}>
            {React.isValidElement(props.promoCode) &&
                React.cloneElement(props.promoCode as React.ReactElement, {
                    couponCustomErrors
                })}
            {orderSummaryLines.subtotal}
            {orderSummaryLines.shipping}
            {locale.toLowerCase() == 'en-gb' ? _renderCustomTaxLine(props) : orderSummaryLines.tax}
            {orderSummaryLines.otherCharge}
            {orderSummaryLines.totalDiscounts ? orderSummaryLines.totalDiscounts : null}
            {orderSummaryLines.orderTotal}
        </Node>
    );
};

const RenderCartLinesGroup = (
    props: ICartViewPropsExt & {
        trackRemoveFromCartEventHandler: (cartLine: CartLine | undefined) => void;
        trackAddToWishlistEventHandler: (cartLine: CartLine | undefined) => void;
    }
) => {
    if ((props.cartLinesGroup || false) && props.cartLinesGroup.length > 0) {
        return (
            <div className={`col-xl-7 msc-cart-lines-group order-2 order-xl-1 ${props.CartlinesWrapper.className}`}>
                <AvailablePromotions props={props}></AvailablePromotions>
                <RenderAddGreetingMessage {...props}></RenderAddGreetingMessage>
                {props.cartLinesGroup?.map((cartLines, i) => {
                    return (
                        <div className='msc-cart-lines-group-wraper' key={i}>
                            {/* {_renderCartLinesGroupHeader(cartlines[0],props.storeSelector, resources, _countItems(cartlines),props.context.actionContext.requestContext.channel?.EmailDeliveryModeCode)} */}
                            {_renderCartlines(
                                cartLines,
                                props.resources,
                                props.storeSelector,
                                props.backToShoppingButton,
                                props.waitingComponent,
                                props.cartLoadingStatus,
                                props.cartDataResult,
                                props.trackRemoveFromCartEventHandler,
                                props.trackAddToWishlistEventHandler,
                                props.telemetryContent,
                                props.multiplePickUpEnabled,
                                props.context.actionContext.requestContext.channel?.EmailDeliveryModeCode,
                                props.context
                            )}
                        </div>
                    );
                })}
            </div>
        );
    } else {
        return props.cartDataResult ? (
            <div className={`col-xl-7 msc-cart-lines-group order-2 order-xl-1 ${props.CartlinesWrapper.className}`}>
                {/*lassName='msc-cart__empty-cart'*/}
                <div>
                    <p className='msc-cart-line'>{props.resources.emptyCartText}</p>
                    {props.backToShoppingButton}
                </div>
            </div>
        ) : (
            <>{props.waitingComponent}</>
        );
    }
};

const _renderGroupBOPISBlock = (
    cartLine: ICartlinesViewProps,
    resources: ICartResources,
    storeSelector: React.ReactNode | undefined,
    telemetryContent?: ITelemetryContent,
    emailDeliveryModeCode?: string
): JSX.Element | null => {
    // If it is electronic item cart line, then return null for GroupBOPISBlock.
    if (!cartLine.pickUpInStore || !storeSelector || cartLine.data?.cartline.DeliveryMode === emailDeliveryModeCode) {
        return null;
    }

    const { changeStoreDisplayText, shipInsteadDisplayText, pickItUpDisplayText } = resources;

    const isBopisSelected = cartLine.pickUpInStore.isBopisSelected;
    const toggleBopis = (event: React.MouseEvent<HTMLElement>) => {
        return cartLine.pickUpInStore && cartLine.pickUpInStore.callbacks.toggleBopis(!isBopisSelected);
    };
    const changeStore = (event: React.MouseEvent<HTMLElement>) => {
        return cartLine.pickUpInStore && cartLine.pickUpInStore.callbacks.toggleBopis(true);
    };
    const payLoad = getPayloadObject('click', telemetryContent!, TelemetryConstant.PickupInStore);
    const puckUpinStoreAttribute = getTelemetryAttributes(telemetryContent!, payLoad);

    return (
        <React.Fragment>
            {isBopisSelected ? (
                <Button className='msc-cart-line__remove-item msc-btn' onClick={changeStore}>
                    {changeStoreDisplayText}
                </Button>
            ) : null}
            <Button className='msc-cart-line__remove-item msc-btn' {...puckUpinStoreAttribute} onClick={toggleBopis}>
                {isBopisSelected ? shipInsteadDisplayText : pickItUpDisplayText}
            </Button>
        </React.Fragment>
    );
};

const _renderRemoveButton = (
    cartLineView: ICartlinesViewProps,
    trackRemoveFromCartEventHandler: (cartLine: CartLine | undefined) => void
) => {
    const removeEl = cartLineView.remove;

    if (!removeEl) return removeEl;

    const enhancedOnClickHandler = (event: any) => {
        // Call the original onClick handler
        // @ts-ignore
        if (typeof removeEl.props.onClick === 'function') {
            // @ts-ignore
            removeEl.props.onClick(event);
        }
        // Add more functionality after onClick if needed
        trackRemoveFromCartEventHandler(cartLineView.data?.cartline);
    };

    // Clone the remove prop, replace the onClick handler with the enhanced handler, reset children and title to remove text (icon only)
    // @ts-ignore
    const enhancedRemoveButton = React.cloneElement(removeEl, {
        onClick: enhancedOnClickHandler,
        children: undefined,
        title: undefined,
    });

    return <>{enhancedRemoveButton}</>;
};

const _renderWishlistButton = (
    cartLineView: ICartlinesViewProps,
    trackAddToWishlistEventHandler: (cartLine: CartLine | undefined) => void
) => {
    const addToWishlistEl = cartLineView.addToWishlist;

    if (!addToWishlistEl) return addToWishlistEl;

    const enhancedOnSuccessHandler = (result: IWishlistActionSuccessResult, cartlineId: CartLine) => {
        // Call the original onSuccess handler
        // @ts-ignore
        if (typeof addToWishlistEl.props.onSuccess === 'function') {
            // @ts-ignore
            addToWishlistEl.props.onSuccess(result, cartlineId);
        }
        // Add more functionality after onSuccess if needed
        trackAddToWishlistEventHandler(cartlineId);
    };

    // Clone the wishlist button prop and replace the onSuccess handler with the enhanced handler
    // @ts-ignore
    const enhancedWishlistButton = React.cloneElement(addToWishlistEl, {
        onSuccess: enhancedOnSuccessHandler
    });

    return <>{enhancedWishlistButton}</>;
};

interface IStickyCheckoutBarProps {
    checkoutButton: React.ReactNode;
    guestCheckoutButton: React.ReactNode;
    isPickMixOOS: boolean;
    onCheckoutClick: () => void;
    onGuestCheckoutClick: () => void;
    resources: ICartResourcesExt;
}

const StickyCheckoutBar: React.FC<IStickyCheckoutBarProps> = ({
    checkoutButton,
    guestCheckoutButton,
    isPickMixOOS
}) => {
    const hasGuestCheckout = Boolean(guestCheckoutButton);
    const stickyBarClass = `msc-cart-sticky-bar ${!hasGuestCheckout ? 'single-button' : ''}`;

    // Clone the existing checkout button with sticky bar styling
    const stickyCheckoutButton = React.isValidElement(checkoutButton) ? 
        React.cloneElement(checkoutButton as React.ReactElement, {
            className: 'msc-cart-sticky-bar__checkout',
            disabled: isPickMixOOS || checkoutButton.props.disabled
        }) : null;

    // Clone the existing guest checkout button with sticky bar styling
    const stickyGuestCheckoutButton = React.isValidElement(guestCheckoutButton) ? 
        React.cloneElement(guestCheckoutButton as React.ReactElement, {
            className: 'msc-cart-sticky-bar__guest-checkout',
            disabled: isPickMixOOS || guestCheckoutButton.props.disabled
        }) : null;

    return (
        <div className={stickyBarClass}>
            {stickyGuestCheckoutButton}
            {stickyCheckoutButton}
        </div>
    );
};

export default CartView;
